import { z } from 'zod'

/**
 * Configuração de ambiente validada com zod.
 * Só variáveis VITE_* (públicas por definição: vão para o bundle). Nenhum segredo aqui.
 */

const booleanString = z
  .string()
  .optional()
  .transform((value) => (value ?? 'false').trim().toLowerCase() === 'true')

const optionalUrl = z
  .string()
  .optional()
  .transform((value) => (value ?? '').trim())
  .pipe(z.union([z.literal(''), z.string().url()]))

const rawEnvSchema = z.object({
  VITE_API_BASE_URL: optionalUrl,
  VITE_SOCKET_URL: optionalUrl,
  VITE_OIDC_AUTHORITY: optionalUrl,
  VITE_OIDC_CLIENT_ID: z
    .string()
    .optional()
    .transform((value) => (value ?? '').trim()),
  VITE_AUTH_MODE: z.enum(['oidc', 'mock']).optional(),
  VITE_USE_MOCK_API: booleanString,
  VITE_API_TIMEOUT_MS: z.coerce.number().int().positive().max(300_000).optional(),
})

export type AuthMode = 'oidc' | 'mock'

export interface AppEnv {
  /** Base da API sem barra final. Vazio = mesma origem da página. */
  apiBaseUrl: string
  /** URL do Socket.IO. Vazio = mesma origem da API. */
  socketUrl: string
  oidcAuthority: string
  oidcClientId: string
  authMode: AuthMode
  useMockApi: boolean
  apiTimeoutMs: number
}

export type EnvParseResult = { ok: true; env: AppEnv } | { ok: false; errors: string[] }

const stripSlash = (value: string) => value.replace(/\/+$/, '')

export function parseEnv(raw: Record<string, string | undefined>): EnvParseResult {
  const parsed = rawEnvSchema.safeParse(raw)
  if (!parsed.success) {
    const errors = Object.entries(parsed.error.flatten().fieldErrors).map(
      ([key, messages]) => `${key}: ${(messages ?? []).join(', ')}`,
    )
    return { ok: false, errors }
  }
  const data = parsed.data
  const useMockApi = data.VITE_USE_MOCK_API
  const authMode: AuthMode = data.VITE_AUTH_MODE ?? (useMockApi ? 'mock' : 'oidc')
  const errors: string[] = []

  if (authMode === 'oidc') {
    if (!data.VITE_OIDC_AUTHORITY) errors.push('VITE_OIDC_AUTHORITY é obrigatório no modo oidc')
    if (!data.VITE_OIDC_CLIENT_ID) errors.push('VITE_OIDC_CLIENT_ID é obrigatório no modo oidc')
  }
  if (authMode === 'mock' && !useMockApi) {
    // Evita publicar um build com login falso apontando para uma API real.
    errors.push('VITE_AUTH_MODE=mock exige VITE_USE_MOCK_API=true')
  }
  if (errors.length > 0) return { ok: false, errors }

  const apiBaseUrl = stripSlash(data.VITE_API_BASE_URL)
  return {
    ok: true,
    env: {
      apiBaseUrl,
      socketUrl: stripSlash(data.VITE_SOCKET_URL) || apiBaseUrl,
      oidcAuthority: stripSlash(data.VITE_OIDC_AUTHORITY),
      oidcClientId: data.VITE_OIDC_CLIENT_ID,
      authMode,
      useMockApi,
      apiTimeoutMs: data.VITE_API_TIMEOUT_MS ?? 20_000,
    },
  }
}

export const envResult: EnvParseResult = parseEnv({
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_SOCKET_URL: import.meta.env.VITE_SOCKET_URL,
  VITE_OIDC_AUTHORITY: import.meta.env.VITE_OIDC_AUTHORITY,
  VITE_OIDC_CLIENT_ID: import.meta.env.VITE_OIDC_CLIENT_ID,
  VITE_AUTH_MODE: import.meta.env.VITE_AUTH_MODE || undefined,
  VITE_USE_MOCK_API: import.meta.env.VITE_USE_MOCK_API,
  VITE_API_TIMEOUT_MS: import.meta.env.VITE_API_TIMEOUT_MS || undefined,
})

/** Valores seguros usados só para a tela de erro de configuração não quebrar. */
const FALLBACK_ENV: AppEnv = {
  apiBaseUrl: '',
  socketUrl: '',
  oidcAuthority: '',
  oidcClientId: '',
  authMode: 'mock',
  useMockApi: true,
  apiTimeoutMs: 20_000,
}

export const env: AppEnv = envResult.ok ? envResult.env : FALLBACK_ENV

export const APP_NAME = 'Woctools'
export const APP_SUBTITLE = 'Back-office GoVendas'
